import { useEffect, useState } from "react";
import {
  BrandInstagram,
  Calendar,
  ChevronRight,
  Confetti,
  Earth,
  Envelope,
  Telephone,
} from "@mynaui/icons-react";
import { Plus, Minus } from "lucide-react";
import { IPlayer } from "../../models/IPlayer";
import lifeLogo from "../../assets/images/life-logo.png";
import { BalanceTransactionDTO } from "../../models/IBalance";
import { ClashPointsBalanceDTO, ClashPointsTransactionDTO } from "../../models/IClashPoints";
import { getUserBalance, getUserTransactions } from "../../api/balance";
import { getUserPoints, getUserPointsTransactions } from "../../api/points";
import { banPlayer, unbanPlayer } from "../../api/players";
import AdjustBalanceDialog from "../AdjustBalanceDialog";
import AdjustPointsDialog from "../AdjustPointsDialog";
import BanPlayerDialog from "../BanPlayerDialog";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { canAdjustClashPoints } from "../../utils/authorization";
import { Button, Card, EmptyState, Loader, PageHeader, TableWrap, Tabs, Tag, TagTone } from "../../ui";
import { PersonMeta, PersonProfile, ProfileLine } from "./PersonProfile";
import "./styles.scss";

type PlayerDetailProps = {
  item: IPlayer;
  onBack: (count: number) => void;
  onUpdate?: () => void;
};

const TOURNAMENTS_COLUMNS = [{ label: "name" }, { label: "type" }, { label: "category" }, { label: "location" }];
const TEAMS_COLUMNS = [{ label: "team name" }, { label: "tournament" }, { label: "category" }, { label: "rank" }];
const REPRESENTERS_COLUMNS = [
  { label: "name" },
  { label: "nationality" },
  { label: "phone number" },
  { label: "email" },
];
const AWARDS_COLUMNS = [{ label: "award name" }, { label: "tournament" }, { label: "category" }, { label: "date" }];
const TRANSACTIONS_COLUMNS = [
  { label: "date" },
  { label: "type" },
  { label: "description" },
  { label: "amount" },
  { label: "balance after" },
  { label: "status" },
];
const POINTS_COLUMNS = [
  { label: "date" },
  { label: "type" },
  { label: "description" },
  { label: "amount" },
  { label: "balance after" },
];

type TabType = "tournaments" | "teams" | "representers" | "awards" | "transactions" | "points";

const TAB_OPTIONS: { value: TabType; label: string }[] = [
  { value: "tournaments", label: "Tournaments" },
  { value: "teams", label: "Teams" },
  { value: "representers", label: "Representers" },
  { value: "awards", label: "Awards" },
  { value: "transactions", label: "Transactions" },
  { value: "points", label: "Points" },
];

const PlayerDetail = ({ item, onBack, onUpdate }: PlayerDetailProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>("tournaments");
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<BalanceTransactionDTO[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustType, setAdjustType] = useState<"credit" | "debit">("credit");
  const [showBanDialog, setShowBanDialog] = useState(false);
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const canAdjustPoints = canAdjustClashPoints(currentUser);
  const [points, setPoints] = useState<ClashPointsBalanceDTO | null>(null);
  const [pointsTransactions, setPointsTransactions] = useState<ClashPointsTransactionDTO[]>([]);
  const [loadingPointsTransactions, setLoadingPointsTransactions] = useState(false);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [pointsAdjustType, setPointsAdjustType] = useState<"credit" | "debit">("credit");

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);

    const count = searchParams.get("count");

    searchParams.set("tab", tab);
    searchParams.set("count", `${Number(count) + 1}`);

    setSearchParams(searchParams);
  };

  const handleBack = () => {
    const count = searchParams.get("count");
    onBack(Number(count));
  };

  const fetchBalance = async () => {
    try {
      const balanceRes = await getUserBalance(item.userId);
      setBalance(balanceRes.data.balance);
    } catch (err: any) {
      console.error("Failed to fetch balance", err);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const transactionsRes = await getUserTransactions(item.userId);
      setTransactions(transactionsRes.data);
    } catch (err: any) {
      console.error("Failed to fetch transactions", err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchPoints = async () => {
    try {
      const pointsRes = await getUserPoints(item.userId);
      setPoints(pointsRes.data);
    } catch (err: any) {
      console.error("Failed to fetch points", err);
    }
  };

  const fetchPointsTransactions = async () => {
    setLoadingPointsTransactions(true);
    try {
      const res = await getUserPointsTransactions(item.userId);
      setPointsTransactions(res.data);
    } catch (err: any) {
      console.error("Failed to fetch points transactions", err);
    } finally {
      setLoadingPointsTransactions(false);
    }
  };

  const handleAdjustDialogClose = (updated?: boolean) => {
    setShowAdjustDialog(false);
    if (updated) {
      dispatch(
        setGeneralAlertMessage({
          type: "success",
          message: `Balance ${adjustType === "credit" ? "credited" : "debited"} successfully`,
        })
      );
      fetchBalance();
      if (activeTab === "transactions") {
        fetchTransactions();
      }
    }
  };

  const handlePointsDialogClose = (updated?: boolean) => {
    setShowPointsDialog(false);
    if (updated) {
      dispatch(
        setGeneralAlertMessage({
          type: "success",
          message: `Clash Points ${pointsAdjustType === "credit" ? "credited" : "debited"} successfully`,
        })
      );
      fetchPoints();
      if (activeTab === "points") {
        fetchPointsTransactions();
      }
    }
  };

  const getPointsTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      Earn: "Earn",
      Conversion: "Conversion",
      Expiry: "Expiry",
      Clawback: "Clawback",
      AdminCredit: "Admin Credit",
      AdminDebit: "Admin Debit",
    };
    return labels[type] || type;
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      InstaPay: "InstaPay Top-Up",
      Fawry: "Fawry Top-Up",
      AdminCredit: "Admin Credit",
      AdminDebit: "Admin Debit",
      TournamentPayment: "Tournament Payment",
      ReservationPayment: "Reservation Payment",
      Refund: "Refund",
    };
    return labels[type] || type;
  };

  const getStatusTone = (status: string): TagTone => {
    switch (status) {
      case "Approved":
        return "ok";
      case "Pending":
        return "warn";
      case "Rejected":
        return "danger";
      default:
        return "neutral";
    }
  };

  useEffect(() => {
    const tab = searchParams.get("tab") as TabType | null;
    if (tab) {
      setActiveTab(tab);
    }
    fetchBalance();
    fetchPoints();
  }, []);

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    } else if (activeTab === "points") {
      fetchPointsTransactions();
    }
  }, [activeTab]);

  // Sort tournaments by startDate descending (latest first)
  const sortedTournaments = [...item.tournaments].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const fullName = `${item.firstName} ${item.lastName ?? ""}`;

  const contacts: ProfileLine[] = [{ key: "email", icon: <Envelope size={20} />, content: item.email }];
  if (item.phoneNumber) contacts.push({ key: "phone", icon: <Telephone size={20} />, content: item.phoneNumber });
  if (item.instagramAccount)
    contacts.push({
      key: "instagram",
      icon: <BrandInstagram size={20} />,
      content: (
        <a href={`https://instagram.com/${item.instagramAccount.replace("@", "")}`} target="_blank" rel="noreferrer">
          @{item.instagramAccount.replace("@", "")}
        </a>
      ),
    });

  const facts: ProfileLine[] = [];
  if (item.birthDate)
    facts.push({ key: "born", icon: <Confetti size={20} />, content: `Born ${moment(item.birthDate).format("DD MMM YYYY")}` });
  if (item.languages.length > 0)
    facts.push({ key: "speaks", icon: <Earth size={20} />, content: `Speaks ${item.languages.map((x) => x.name).join(", ")}` });
  facts.push({ key: "joined", icon: <Calendar size={20} />, content: `Joined ${moment(item.createdAt).format("MMM YYYY")}` });

  const openCell = (
    <td className="ui-table__actions">
      <ChevronRight size={20} className="player-detail__chevron" />
    </td>
  );

  return (
    <div className="player-detail">
      <PageHeader
        back={{ onClick: handleBack, label: "Back" }}
        title={fullName}
        subtitle={<PersonMeta birthDate={item.birthDate} gender={item.gender} nationality={item.nationality} />}
        actions={
          <Button variant={item.isBanned ? "tint" : "danger-tint"} onClick={() => setShowBanDialog(true)}>
            {item.isBanned ? "Unban Player" : "Ban Player"}
          </Button>
        }
      >
        <div className="player-detail__badges">
          {item.isLifeMember && <img src={lifeLogo} alt="Life Member" className="player-detail__life-logo" />}
          <Tag tone={item.emailVerified ? "ok" : "warn"}>{item.emailVerified ? "Verified" : "Unverified"}</Tag>
          {item.isBanned && <Tag tone="danger">Banned</Tag>}
        </div>
      </PageHeader>

      <PersonProfile name={fullName} photo={item.photo} contacts={contacts} facts={facts} />

      <div className="player-detail__wallets">
        <Card
          title="Balance"
          actions={
            <>
              <Button
                size="sm"
                variant="tint"
                icon={<Plus size={14} />}
                onClick={() => {
                  setAdjustType("credit");
                  setShowAdjustDialog(true);
                }}
              >
                Credit
              </Button>
              <Button
                size="sm"
                variant="danger-tint"
                icon={<Minus size={14} />}
                onClick={() => {
                  setAdjustType("debit");
                  setShowAdjustDialog(true);
                }}
              >
                Debit
              </Button>
            </>
          }
        >
          <p className="player-detail__amount">{balance !== null ? `${balance.toLocaleString()} EGP` : "—"}</p>
        </Card>
        <Card
          title="Clash Points"
          actions={
            canAdjustPoints ? (
              <>
                <Button
                  size="sm"
                  variant="tint"
                  icon={<Plus size={14} />}
                  onClick={() => {
                    setPointsAdjustType("credit");
                    setShowPointsDialog(true);
                  }}
                >
                  Credit
                </Button>
                <Button
                  size="sm"
                  variant="danger-tint"
                  icon={<Minus size={14} />}
                  onClick={() => {
                    setPointsAdjustType("debit");
                    setShowPointsDialog(true);
                  }}
                >
                  Debit
                </Button>
              </>
            ) : undefined
          }
        >
          <div className="player-detail__points">
            <p className="player-detail__amount">{points ? `${points.available.toLocaleString()} pts` : "—"}</p>
            {points && points.expiringSoon > 0 && (
              <Tag tone="warn">
                {points.expiringSoon.toLocaleString()} expiring
                {points.nextExpiryDate ? ` by ${moment(points.nextExpiryDate).format("DD MMM YYYY")}` : " soon"}
              </Tag>
            )}
          </div>
        </Card>
      </div>

      <div className="player-detail__data">
        <Tabs options={TAB_OPTIONS} value={activeTab} onChange={handleTabClick} ariaLabel="Player sections" />
        <div className="player-detail__tab-content">
          {activeTab === "points" ? (
            loadingPointsTransactions ? (
              <Loader minHeight={200} />
            ) : pointsTransactions.length > 0 ? (
              <TableWrap>
                <table className="ui-table">
                  <thead>
                    <tr>
                      {POINTS_COLUMNS.map((col, id) => (
                        <th key={id} className={col.label === "amount" || col.label === "balance after" ? "ui-table__num" : undefined}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pointsTransactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{moment(tx.createdAt).format("DD MMM YYYY, hh:mm A")}</td>
                        <td>{getPointsTypeLabel(tx.type)}</td>
                        <td>{tx.description || "—"}</td>
                        <td className={`ui-table__num ${tx.amount >= 0 ? "player-detail__plus" : "player-detail__minus"}`}>
                          {tx.amount >= 0 ? "+" : ""}
                          {tx.amount.toLocaleString()} pts
                        </td>
                        <td className="ui-table__num">{tx.balanceAfter.toLocaleString()} pts</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <Card padding="none">
<EmptyState title="No points activity" description="This player has no Clash Points transactions yet" />
</Card>
            )
          ) : activeTab === "transactions" ? (
            loadingTransactions ? (
              <Loader minHeight={200} />
            ) : transactions.length > 0 ? (
              <TableWrap>
                <table className="ui-table">
                  <thead>
                    <tr>
                      {TRANSACTIONS_COLUMNS.map((col, id) => (
                        <th key={id} className={col.label === "amount" || col.label === "balance after" ? "ui-table__num" : undefined}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{moment(tx.createdAt).format("DD MMM YYYY, hh:mm A")}</td>
                        <td>{getTransactionTypeLabel(tx.type)}</td>
                        <td>{tx.description || "—"}</td>
                        <td className={`ui-table__num ${tx.amount >= 0 ? "player-detail__plus" : "player-detail__minus"}`}>
                          {tx.amount >= 0 ? "+" : ""}
                          {tx.amount.toLocaleString()} EGP
                        </td>
                        <td className="ui-table__num">{tx.balanceAfter.toLocaleString()} EGP</td>
                        <td>
                          <Tag tone={getStatusTone(tx.status)}>{tx.status}</Tag>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <Card padding="none">
<EmptyState title="No transactions" description="This player has no balance transactions yet" />
</Card>
            )
          ) : (item.tournaments.length > 0 && (activeTab === "tournaments" || activeTab === "teams")) ||
            (item.coaches.length > 0 && activeTab === "representers") ||
            (item.awards.length > 0 && activeTab === "awards") ? (
            <TableWrap>
              <table className="ui-table">
                <thead>
                  <tr>
                    {(activeTab === "tournaments"
                      ? TOURNAMENTS_COLUMNS
                      : activeTab === "teams"
                      ? TEAMS_COLUMNS
                      : activeTab === "representers"
                      ? REPRESENTERS_COLUMNS
                      : activeTab === "awards"
                      ? AWARDS_COLUMNS
                      : []
                    ).map((item, id) => (
                      <th key={id}>{item.label}</th>
                    ))}
                    <th className="ui-table__actions" />
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let data: any[] = [];
                    if (activeTab === "tournaments") {
                      data = sortedTournaments;
                    } else if (activeTab === "teams") {
                      data = sortedTournaments.flatMap((t) =>
                        t.categories.flatMap((c) =>
                          c.teams.map((team) => ({
                            ...team,
                            tournamentName: t.name,
                            tournamentId: t.id,
                            tournamentStartDate: t.startDate,
                            tournamentCategoryId: t.categories?.find((x) => x.categoryName === team.categoryName)?.id,
                          }))
                        )
                      );
                    } else if (activeTab === "representers") {
                      data = item.coaches;
                    } else if (activeTab === "awards") {
                      data = item.awards;
                    }

                    return data.map((row, rowIndex) => {
                      if (activeTab === "tournaments") {
                        const tournament = row as (typeof item.tournaments)[number];
                        return (
                          <tr
                            key={rowIndex}
                            className="is-clickable"
                            onClick={() =>
                              navigate(`/dashboard/tournaments/${tournament.id}?goTo=player&playerId=${item.id}`)
                            }
                          >
                            <td className="ui-table__primary">{tournament.name}</td>
                            <td>{tournament.type}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {tournament.categories.map((category) => (
                                // An inline text link inside the row (the row itself opens the tournament)
                                <button
                                  type="button"
                                  key={category.id}
                                  className="player-detail__category"
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/tournaments/${tournament.id}/${category.id}?goTo=player&playerId=${item.id}`
                                    )
                                  }
                                >
                                  {category.categoryName}
                                </button>
                              ))}
                            </td>
                            <td>{tournament.location?.name || "-"}</td>
                            {openCell}
                          </tr>
                        );
                      }

                      if (activeTab === "teams") {
                        const team = row as any;
                        return (
                          <tr
                            key={rowIndex}
                            className="is-clickable"
                            onClick={() =>
                              navigate(
                                `/dashboard/tournaments/${team.tournamentId}/${team.tournamentCategoryId}/teams/${team.teamId}`
                              )
                            }
                          >
                            <td className="ui-table__primary">{team.teamName}</td>
                            <td>{team.tournamentName}</td>
                            <td>{team.categoryName}</td>
                            <td>{team.rank ?? "N/A"}</td>
                            {openCell}
                          </tr>
                        );
                      }

                      if (activeTab === "representers") {
                        const coach = row as (typeof item.coaches)[number];
                        return (
                          <tr
                            key={rowIndex}
                            className="is-clickable"
                            onClick={() => navigate(`/dashboard/representers/${coach.id}`)}
                          >
                            <td className="ui-table__primary">
                              {coach.firstName} {coach.lastName ?? ""}
                            </td>
                            <td>{coach.nationality}</td>
                            <td>{coach.phoneNumber ?? "-"}</td>
                            <td>{coach.email}</td>
                            {openCell}
                          </tr>
                        );
                      }

                      if (activeTab === "awards") {
                        const award = row as (typeof item.awards)[number];
                        return (
                          <tr key={rowIndex} className="is-clickable" onClick={() => navigate(`/dashboard/awards/${award.id}`)}>
                            <td className="ui-table__primary">{award.name}</td>
                            <td>{award.tournamentName}</td>
                            <td>{award.categoryName}</td>
                            <td>{moment(award.date).format("DD MMM YYYY")}</td>
                            {openCell}
                          </tr>
                        );
                      }

                      return null;
                    });
                  })()}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Card padding="none">
<EmptyState
              title={`No ${activeTab}`}
              description={`This player currently has no ${activeTab} ${
                activeTab !== "representers" && activeTab !== "awards" ? "history" : ""
              }`}
            />
</Card>
          )}
        </div>
      </div>

      <AdjustBalanceDialog
        isOpen={showAdjustDialog}
        type={adjustType}
        userId={item.userId}
        playerName={`${item.firstName} ${item.lastName ?? ""}`.trim()}
        onClose={handleAdjustDialogClose}
      />

      <AdjustPointsDialog
        isOpen={showPointsDialog}
        type={pointsAdjustType}
        userId={item.userId}
        playerName={`${item.firstName} ${item.lastName ?? ""}`.trim()}
        onClose={handlePointsDialogClose}
      />

      <BanPlayerDialog
        isOpen={showBanDialog}
        isBanned={item.isBanned}
        playerName={`${item.firstName} ${item.lastName ?? ""}`.trim()}
        onBan={async (reason) => {
          await banPlayer(item.id, reason);
          dispatch(setGeneralAlertMessage({ type: "success", message: "Player banned successfully" }));
          onUpdate?.();
        }}
        onUnban={async () => {
          await unbanPlayer(item.id);
          dispatch(setGeneralAlertMessage({ type: "success", message: "Player unbanned successfully" }));
          onUpdate?.();
        }}
        onClose={() => setShowBanDialog(false)}
      />
    </div>
  );
};

export default PlayerDetail;
